import { useEffect, useState } from 'react';
import { LANGS, type MessageKey, setLanguage, useT } from '../i18n';
import {
  type GraphicsPreset,
  type GraphicsSettings,
  presetSettings,
  type VegetationQuality,
} from '../render/graphics';
import { type Controls, savePrefs, useGame } from '../store';
import { Icon, type IconName } from './icons';
import { MixSlider, VolumeSlider } from './VolumeSlider';

/*
 * Settings, in the main menu and in the game (gear → All settings): graphics profiles from a
 * "potato" for old laptops to ultra, every graphics option on its own, the game's handling
 * (frame-rate meter, camera, autosave, notifications), sound and language. Changes apply at once:
 * the game listens to the settings (game.ts).
 */

const PRESETS: { id: GraphicsPreset; icon: IconName }[] = [
  { id: 'potato', icon: 'monitor' },
  { id: 'low', icon: 'monitor' },
  { id: 'medium', icon: 'monitor' },
  { id: 'high', icon: 'star' },
  { id: 'ultra', icon: 'crown' },
];

/** Stats of the running game's renderer, for the live frame rate in the graphics tab. */
export interface LiveStats {
  fps: number;
  drawCalls: number;
  triangles: number;
  backend: string;
}

type Tab = 'graphics' | 'game' | 'sound';

function setGraphics(change: Partial<GraphicsSettings>): void {
  const g = useGame.getState().graphics;
  useGame.setState({ graphics: { ...g, ...change, preset: 'custom' } });
  savePrefs(useGame.getState());
}

function setPreset(preset: GraphicsPreset): void {
  useGame.setState({ graphics: presetSettings(preset) });
  savePrefs(useGame.getState());
}

function setControls(change: Partial<Controls>): void {
  useGame.setState((s) => ({ controls: { ...s.controls, ...change } }));
  savePrefs(useGame.getState());
}

/** A row of keys, one lit (a selector switch). */
function Choice<T extends string | number>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="segmented settings-choice">
      {options.map((o) => (
        <button
          type="button"
          key={String(o.value)}
          className={o.value === value ? 'active' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** An on/off toggle switch. */
function Toggle({
  on,
  onChange,
  label,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`settings-toggle ${on ? 'on' : ''}`}
      onClick={() => onChange(!on)}
    >
      <span className="settings-toggle-knob" />
    </button>
  );
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="settings-row">
      <div className="settings-row-text">
        <span>{label}</span>
        {hint && <small className="muted">{hint}</small>}
      </div>
      <div className="settings-row-control">{children}</div>
    </div>
  );
}

function GraphicsTab({ stats }: { stats?: () => LiveStats }) {
  const t = useT();
  const g = useGame((s) => s.graphics);
  const [live, setLive] = useState<LiveStats | null>(null);
  useEffect(() => {
    if (!stats) return;
    setLive({ ...stats() });
    const id = window.setInterval(() => setLive({ ...stats() }), 500);
    return () => window.clearInterval(id);
  }, [stats]);
  const levels5 = (prefix: string) =>
    (['off', 'low', 'medium', 'high', 'ultra'] as const).map((v) => ({
      value: v,
      label: t(`${prefix}.${v}` as MessageKey),
    }));
  return (
    <div className="settings-page">
      {live && (
        <div className="settings-live">
          <span
            className={`settings-fps ${live.fps >= 50 ? 'ok' : live.fps >= 28 ? 'warn' : 'bad'}`}
          >
            {live.fps}
            <small>FPS</small>
          </span>
          <span className="muted small">
            {live.backend} · {live.drawCalls} draw · {(live.triangles / 1000).toFixed(0)}k tris
          </span>
        </div>
      )}
      <div className="settings-presets">
        {PRESETS.map((p) => (
          <button
            type="button"
            key={p.id}
            className={`settings-preset ${g.preset === p.id ? 'active' : ''}`}
            onClick={() => setPreset(p.id)}
          >
            <Icon name={p.icon} size={18} />
            <strong>{t(`graphics.${p.id}` as MessageKey)}</strong>
            <span>{t(`graphics.${p.id}.desc` as MessageKey)}</span>
          </button>
        ))}
      </div>
      {g.preset === 'custom' && <p className="settings-custom">{t('settings.custom')}</p>}
      <h4 className="settings-group">{t('settings.image')}</h4>
      <Row label={t('settings.renderScale')} hint={t('settings.renderScale.hint')}>
        <Choice
          value={g.renderScale}
          options={[0.5, 0.67, 0.75, 0.85, 1].map((v) => ({
            value: v,
            label: `%${Math.round(v * 100)}`,
          }))}
          onChange={(v) => setGraphics({ renderScale: v })}
        />
      </Row>
      <Row label={t('settings.msaa')}>
        <Choice
          value={g.msaa}
          options={[
            { value: 0 as const, label: t('settings.off') },
            { value: 2 as const, label: '2×' },
            { value: 4 as const, label: '4×' },
          ]}
          onChange={(v) => setGraphics({ msaa: v })}
        />
      </Row>
      <Row label={t('settings.fpsCap')} hint={t('settings.fpsCap.hint')}>
        <Choice
          value={g.fpsCap}
          options={[
            { value: 30 as const, label: '30' },
            { value: 60 as const, label: '60' },
            { value: 120 as const, label: '120' },
            { value: 0 as const, label: t('settings.unlimited') },
          ]}
          onChange={(v) => setGraphics({ fpsCap: v })}
        />
      </Row>
      <h4 className="settings-group">{t('settings.lighting')}</h4>
      <Row label={t('settings.bloom')} hint={t('settings.bloom.hint')}>
        <Toggle
          on={g.bloom}
          onChange={(v) => setGraphics({ bloom: v })}
          label={t('settings.bloom')}
        />
      </Row>
      <h4 className="settings-group">{t('settings.world')}</h4>
      <Row label={t('settings.vegetation')} hint={t('settings.vegetation.hint')}>
        <Choice<VegetationQuality>
          value={g.vegetation}
          options={levels5('quality')}
          onChange={(v) => setGraphics({ vegetation: v })}
        />
      </Row>
      <Row label={t('settings.towns')} hint={t('settings.towns.hint')}>
        <Toggle
          on={g.towns}
          onChange={(v) => setGraphics({ towns: v })}
          label={t('settings.towns')}
        />
      </Row>
      <Row label={t('settings.weatherFx')} hint={t('settings.weatherFx.hint')}>
        <Toggle
          on={g.weather}
          onChange={(v) => setGraphics({ weather: v })}
          label={t('settings.weatherFx')}
        />
      </Row>
      <Row label={t('settings.birds')}>
        <Toggle
          on={g.birds}
          onChange={(v) => setGraphics({ birds: v })}
          label={t('settings.birds')}
        />
      </Row>
    </div>
  );
}

function GameTab() {
  const t = useT();
  const c = useGame((s) => s.controls);
  return (
    <div className="settings-page">
      <h4 className="settings-group">{t('settings.interface')}</h4>
      <Row label={t('settings.fpsMeter')} hint={t('settings.fpsMeter.hint')}>
        <Choice
          value={c.fpsMeter}
          options={[
            { value: 'off' as const, label: t('settings.off') },
            { value: 'fps' as const, label: 'FPS' },
            { value: 'full' as const, label: t('settings.detailed') },
          ]}
          onChange={(v) => setControls({ fpsMeter: v })}
        />
      </Row>
      <Row label={t('settings.quietToasts')} hint={t('settings.quietToasts.hint')}>
        <Toggle
          on={c.quietToasts}
          onChange={(v) => setControls({ quietToasts: v })}
          label={t('settings.quietToasts')}
        />
      </Row>
      <Row label={t('settings.delayAlerts')} hint={t('settings.delayAlerts.hint')}>
        <Toggle
          on={c.delayAlerts}
          onChange={(v) => setControls({ delayAlerts: v, mutedDelays: v ? [] : c.mutedDelays })}
          label={t('settings.delayAlerts')}
        />
      </Row>
      <Row label={t('settings.reports')} hint={t('settings.reports.hint')}>
        <Toggle
          on={c.reports}
          onChange={(v) => setControls({ reports: v })}
          label={t('settings.reports')}
        />
      </Row>
      <Row label={t('settings.radio')} hint={t('settings.radio.hint')}>
        <Choice
          value={c.radio}
          options={[
            { value: 'off' as const, label: t('settings.off') },
            { value: 'text' as const, label: t('settings.radio.text') },
          ]}
          onChange={(v) => setControls({ radio: v })}
        />
      </Row>
      <h4 className="settings-group">{t('settings.camera')}</h4>
      <Row label={t('settings.panSpeed')}>
        <Choice
          value={c.panSpeed}
          options={[0.5, 0.75, 1, 1.5, 2].map((v) => ({ value: v, label: `${v}×` }))}
          onChange={(v) => setControls({ panSpeed: v })}
        />
      </Row>
      <Row label={t('settings.edgePan')} hint={t('settings.edgePan.hint')}>
        <Toggle
          on={c.edgePan}
          onChange={(v) => setControls({ edgePan: v })}
          label={t('settings.edgePan')}
        />
      </Row>
      <Row label={t('settings.invertZoom')}>
        <Toggle
          on={c.invertZoom}
          onChange={(v) => setControls({ invertZoom: v })}
          label={t('settings.invertZoom')}
        />
      </Row>
      <h4 className="settings-group">{t('settings.saving')}</h4>
      <Row label={t('settings.autosave')} hint={t('settings.autosave.hint')}>
        <Choice
          value={c.autosaveMinutes}
          options={[
            { value: 0 as const, label: t('settings.off') },
            { value: 2 as const, label: t('settings.minutes', { n: 2 }) },
            { value: 5 as const, label: t('settings.minutes', { n: 5 }) },
            { value: 10 as const, label: t('settings.minutes', { n: 10 }) },
          ]}
          onChange={(v) => setControls({ autosaveMinutes: v })}
        />
      </Row>
    </div>
  );
}

function SoundTab() {
  const t = useT();
  const lang = useGame((s) => s.lang);
  return (
    <div className="settings-page">
      <h4 className="settings-group">{t('settings.volume')}</h4>
      <VolumeSlider />
      <h4 className="settings-group">{t('settings.soundMix')}</h4>
      <MixSlider channel="engines" label={t('settings.mixEngines')} />
      <MixSlider channel="world" label={t('settings.mixWorld')} />
      <MixSlider channel="interface" label={t('settings.mixInterface')} />
      <h4 className="settings-group">{t('menu.language')}</h4>
      <Choice
        value={lang}
        options={LANGS.map((l) => ({ value: l.id, label: l.name }))}
        onChange={(l) => void setLanguage(l)}
      />
    </div>
  );
}

/** The settings with their tabs (the menu's settings page and the in-game window). */
export function SettingsContent({ stats }: { stats?: () => LiveStats }) {
  const t = useT();
  const [tab, setTab] = useState<Tab>('graphics');
  const tabs: { id: Tab; icon: IconName; label: MessageKey }[] = [
    { id: 'graphics', icon: 'monitor', label: 'settings.graphics' },
    { id: 'game', icon: 'gear', label: 'settings.game' },
    { id: 'sound', icon: 'sound', label: 'settings.soundLang' },
  ];
  return (
    <div className="settings">
      <nav className="ops-tabs settings-tabs">
        {tabs.map((x) => (
          <button
            type="button"
            key={x.id}
            className={tab === x.id ? 'active' : ''}
            onClick={() => setTab(x.id)}
          >
            <Icon name={x.icon} size={15} />
            {t(x.label)}
          </button>
        ))}
      </nav>
      {tab === 'graphics' && <GraphicsTab stats={stats} />}
      {tab === 'game' && <GameTab />}
      {tab === 'sound' && <SoundTab />}
    </div>
  );
}

/** The settings as a window over the game (gear → All settings). */
export function SettingsWindow({ stats }: { stats: () => LiveStats }) {
  const t = useT();
  const open = useGame((s) => s.settingsOpen);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') useGame.setState({ settingsOpen: false });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  if (!open) return null;
  return (
    <div
      className="academy-backdrop"
      onPointerDown={(e) =>
        e.target === e.currentTarget && useGame.setState({ settingsOpen: false })
      }
    >
      <section className="settings-window panel" role="dialog" aria-label={t('menu.settings')}>
        <header className="settings-head">
          <span className="window-icon">
            <Icon name="gear" size={20} />
          </span>
          <h2>{t('menu.settings')}</h2>
          <button
            type="button"
            className="icon-btn"
            onClick={() => useGame.setState({ settingsOpen: false })}
            title={t('hud.close')}
          >
            <Icon name="close" size={16} />
          </button>
        </header>
        <SettingsContent stats={stats} />
      </section>
    </div>
  );
}

/** The frame-rate meter in the corner (Settings → Game). */
export function FpsMeter({ stats }: { stats: () => LiveStats }) {
  const mode = useGame((s) => s.controls.fpsMeter);
  const [live, setLive] = useState<LiveStats | null>(null);
  useEffect(() => {
    if (mode === 'off') return;
    // A copy each time: the renderer updates one stats object in place, which React would take
    // for no change (and the meter would never move).
    setLive({ ...stats() });
    // The renderer measures over five seconds; the meter shows each new reading.
    const id = window.setInterval(() => setLive({ ...stats() }), 1000);
    return () => window.clearInterval(id);
  }, [mode, stats]);
  if (mode === 'off' || !live) return null;
  const tone = live.fps >= 50 ? 'ok' : live.fps >= 28 ? 'warn' : 'bad';
  return (
    <div className={`fps-meter ${tone}`} aria-hidden="true">
      <strong>{live.fps}</strong>
      <span>FPS</span>
      {mode === 'full' && (
        <em>
          {live.backend} · {live.drawCalls} dc · {(live.triangles / 1000).toFixed(0)}k
        </em>
      )}
    </div>
  );
}
